import React, { useMemo, useState } from 'react';
import { Message } from '../../../models/Message';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { Avatar, Button, Tabs, Typography } from 'antd';
import { MailFolderId, ProjectId } from '../../../models/Types';
import { EventDetails } from '../../calendar/components/EventDetails';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setMailListNavigationState } from '../actions/actionControllers/mailNavigationActionController';
import MessageView from './MessageView';
import classNames from 'classnames';
import { parseLocation, readableMessageEventTime } from '../../calendar/util';
import {
  getMailSettings,
  getProject,
  getUserMe,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { Project } from '../../../models/Project';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { PillCRA as Pill } from '@prio365/prio365-react-library';
import EventMessageViewHeader from './EventMessageViewHeader';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  header: {
    padding: `${theme.old.spacing.defaultPadding}px ${theme.old.spacing.defaultPadding}px 0`,
    backgroundColor: theme.old.palette.backgroundPalette.content,
    borderBottom: theme.old.borders.sub,
    overflow: 'hidden',
  },
  headerRow: {
    overflow: 'hidden',
  },
  headerRowColumn: {
    overflow: 'hidden',
  },
  headerRowColumnRow: {
    overflow: 'hidden',
  },
  tabs: {
    '& .ant-tabs-nav': {
      margin: 0,
    },
    '& .ant-tabs-nav::before': {
      border: 'none',
    },
    '& .ant-tabs-content-holder': {
      display: 'none',
    },
    '& .ant-tabs-tab': {
      flex: 'unset',
      maxWidth: 'unset',
      minWidth: 'unset',
    },
  },
  tabsCompressedView: {
    '& .ant-tabs-tab': {
      paddingBottom: '0px',
    },
  },
  title: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: theme.old.spacing.baseSpacing,
  },
  titleLine: {
    overflow: 'hidden',
    '& h3.ant-typography, .ant-typography h3': {
      marginBottom: 0,
    },
  },
  eventOverview: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
  },
  content: {
    height: '100%',
    overflow: 'auto',
  },
  messagesContent: {
    backgroundColor: theme.old.palette.backgroundPalette.sub,
  },
  messageView: {
    height: '100%',
    overflowY: 'auto',
  },
  info: {
    flex: 1,
  },
  icon: {
    marginTop: 4,
  },
}));

interface EventMessageViewProps {
  className?: string;
  projectId: ProjectId;
  message: Message;
  mailFolderId: MailFolderId;
  backButton?: boolean;
  prefix?: string;
  handleReloadMessage?: VoidFunction;
}

export const EventMessageView: React.FC<EventMessageViewProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();

  const {
    className,
    projectId,
    message,
    backButton,
    prefix,
    mailFolderId,
    handleReloadMessage,
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [currentTab, setCurrentTab] = useState<string>('messages');
  const event = message?.event;
  const userMe = useSelector(getUserMe);
  const project = useSelector<RootReducerState, Project>((state) =>
    projectId !== 'me' ? getProject(state, projectId) : null
  );

  const mailAddress = useMemo(() => {
    if (projectId === 'me') {
      return userMe.mail;
    } else if (project) {
      return `${project.eMailPrefix}@${project.eMailSuffix}`;
    }
    return '';
  }, [userMe, project, projectId]);

  const mailSettings = useSelector(getMailSettings);

  const numberOfNewTimeProposals = useMemo(
    () => (event?.attendees || []).filter((att) => att.proposedNewTime).length,
    [event]
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Components
  //#endregion

  //#region ------------------------------ Effects
  //#endregion
  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <Flex.Column className={classes.header}>
        <Flex.Row className={classes.headerRow}>
          <Flex.Column
            className={classes.headerRowColumn}
            flex={1}
            childrenGap={theme.old.spacing.baseSpacing}
          >
            <Flex.Row
              className={classes.headerRowColumnRow}
              alignItems="center"
            >
              {backButton && (
                <Link to={prefix ?? mailFolderId ?? 'inbox'}>
                  <Button
                    type="link"
                    onClick={() => {
                      dispatch(setMailListNavigationState(null, projectId));
                    }}
                    icon={<FontAwesomeIcon icon={['fal', 'chevron-left']} />}
                  />
                </Link>
              )}
              <Flex.Row
                alignItems="center"
                childrenGap={theme.old.spacing.baseSpacing}
                className={classes.titleLine}
              >
                <Avatar
                  style={{
                    backgroundColor: theme.old.palette.primaryColor,
                    minWidth: 32,
                  }}
                  icon={<FontAwesomeIcon icon={['fal', 'calendar']} />}
                />
                <Typography.Title
                  level={3}
                  className={classes.title}
                  title={message?.subject}
                >
                  {message?.subject}
                </Typography.Title>
              </Flex.Row>
            </Flex.Row>
            <Flex.Item>
              <Flex.Row childrenGap={theme.old.spacing.baseSpacing}>
                <FontAwesomeIcon
                  icon={['fal', 'clock']}
                  color={theme.old.typography.colors.muted}
                  style={{ width: theme.old.spacing.unit(4) }}
                  className={classes.icon}
                />
                <Typography.Text className={classes.info}>
                  {readableMessageEventTime(event)}
                </Typography.Text>
              </Flex.Row>
              {(event?.location?.displayName ||
                event?.locations?.length > 0) && (
                <Flex.Row childrenGap={theme.old.spacing.baseSpacing}>
                  <FontAwesomeIcon
                    icon={['fal', 'map-marker-alt']}
                    color={theme.old.typography.colors.muted}
                    style={{ width: theme.old.spacing.unit(4) }}
                    className={classes.icon}
                  />
                  <Typography.Text className={classes.info}>
                    {parseLocation(event?.location, event?.locations)}
                  </Typography.Text>
                </Flex.Row>
              )}
              <Flex.Row childrenGap={theme.old.spacing.baseSpacing}>
                <FontAwesomeIcon
                  icon={['fal', 'user']}
                  color={theme.old.typography.colors.muted}
                  style={{ width: theme.old.spacing.unit(4) }}
                  className={classes.icon}
                />
                <Typography.Text className={classes.info}>
                  {event?.organizer?.emailAddress?.name ??
                    event?.organizer?.emailAddress?.address ??
                    '-'}
                </Typography.Text>
              </Flex.Row>
            </Flex.Item>
          </Flex.Column>
          {event?.organizer &&
            event?.organizer?.emailAddress?.address !== mailAddress && (
              <EventMessageViewHeader
                projectId={projectId}
                messageId={message?.id}
                event={event}
                mailFolderId={mailFolderId}
              />
            )}
        </Flex.Row>
        <Tabs
          className={classNames(classes.tabs, {
            [classes.tabsCompressedView]:
              mailSettings.mailListSpacing === 'tight',
          })}
          activeKey={currentTab}
          onChange={setCurrentTab}
        >
          <Tabs.TabPane
            tab={t('calendar:eventOverview.tabs.messages')}
            key="messages"
          />
          <Tabs.TabPane
            tab={
              <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
                <div>{t('calendar:eventOverview.tabs.details')}</div>
                {numberOfNewTimeProposals > 0 && (
                  <Pill type="primary" size="small">
                    {numberOfNewTimeProposals}
                  </Pill>
                )}
              </Flex.Row>
            }
            key="details"
          />
        </Tabs>
      </Flex.Column>
      <Flex.Item
        flex={1}
        className={classNames(classes.content, {
          [classes.messagesContent]: currentTab === 'messages',
        })}
      >
        {currentTab === 'messages'
          ? [message].map((m) => (
              <MessageView
                message={m}
                projectId={projectId}
                className={classes.messageView}
                isEvent
                showActions
              />
            ))
          : event && (
              <EventDetails
                className={classes.eventOverview}
                event={event}
                projectId={projectId}
                handleReloadMessage={handleReloadMessage}
              />
            )}
      </Flex.Item>
    </Flex.Column>
  );
};
export default EventMessageView;
